/**
 * Word Search Player View
 * Interactive word search puzzle with touch-drag selection
 */

import { t } from '../i18n/config.js';
import { api } from '../services/api.js';
import { getCachedModule, setCachedModule } from '../services/content.js';
import { ensureAudioUnlocked } from '../utils/audioUnlock.js';
import { playWordFoundReward } from '../utils/wordSearchRewardAnimation.js';

/** @type {string|null} */
let currentSessionId = null;

// Pre-created Audio elements for iOS Safari compatibility
// iOS requires Audio objects to be created/played within a user gesture.
// We create them on first grid interaction and reuse them.
let audioBounce = null;
let audioWin = null;
let audioRewardEasy = null;
let audioRewardMedium = null;
let audioRewardHard = null;
let audioInitialized = false;

/**
 * Initialize audio elements on first user gesture (iOS requirement).
 * Must be called within a pointerdown/touchstart/click handler.
 */
function initAudioElements() {
  if (audioInitialized) return;
  audioInitialized = true;
  audioBounce = new Audio('/audio/bounce.mp3');
  audioBounce.volume = 0.5;
  audioWin = new Audio('/audio/win.mp3');
  audioWin.volume = 0.8;
  audioRewardEasy = new Audio('/audio/word-search-easy-word-win.mp3');
  audioRewardEasy.volume = 0.6;
  audioRewardMedium = new Audio('/audio/word-search-medium-word-win.mp3');
  audioRewardMedium.volume = 0.6;
  audioRewardHard = new Audio('/audio/word-search-difficult-word-win.mp3');
  audioRewardHard.volume = 0.6;
  // Play silent to unlock all elements on iOS
  [audioBounce, audioWin, audioRewardEasy, audioRewardMedium, audioRewardHard].forEach(a => {
    a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => { });
  });
}

/**
 * Play the bounce/selection sound effect.
 */
function playBounceSound() {
  if (audioBounce) {
    audioBounce.currentTime = 0;
    audioBounce.play().catch(() => { });
  }
}

/**
 * Play the win/game-complete sound effect.
 */
function playWinSound() {
  if (audioWin) {
    audioWin.currentTime = 0;
    audioWin.play().catch(() => { });
  }
}

/**
 * Play the word-found reward sound for the current difficulty.
 */
function playRewardSoundLocal() {
  let audio = audioRewardMedium;
  if (currentDifficulty === 'easy') audio = audioRewardEasy;
  else if (currentDifficulty === 'hard') audio = audioRewardHard;
  if (audio) {
    audio.currentTime = 0;
    audio.play().catch(() => { });
  }
}
/** @type {string[][]} */
let grid = [];
/** @type {string[]} */
let wordBank = [];
/** @type {Set<string>} */
let foundWords = new Set();
/** @type {number} */
let hintsUsed = 0;
/** @type {number} */
let startTime = 0;
/** @type {{row: number, col: number}[]} */
let selectedCells = [];
/** @type {boolean} */
let isDragging = false;
/** @type {{word: string, row: number, col: number}[]} */
let hintPositions = [];

/** @type {string} Current game difficulty */
let currentDifficulty = 'medium';

/**
 * Render word search player interface
 * @param {object} profile - Current child profile
 * @param {object} module - Word search module data
 */
export async function renderWordSearchPlayer(profile, module) {
  const contentContainer = document.getElementById('content-container');

  if (!contentContainer) {
    console.error('content-container not found');
    return;
  }

  // Reset state
  currentSessionId = null;
  grid = [];
  wordBank = [];
  foundWords = new Set();
  selectedCells = [];
  isDragging = false;
  colorIndex = 0;
  hintPositions = [];

  // Load persisted hints for this profile + module from localStorage
  hintsUsed = getStoredHints(profile.id, module.id);

  // Show Kassie loader while puzzle is being generated
  window.showLoader();

  try {
    // Check for a cached board in kassie_module_{moduleId}.wordSearch
    const cached = getCachedModule(module.id);
    const storedBoard = cached?.wordSearch || null;

    let response;

    if (storedBoard && storedBoard.grid && storedBoard.wordBank && storedBoard.rows && storedBoard.cols) {
      // Use stored board — skip API call
      console.log('🔄 Restoring word search board from cache');
      response = storedBoard;
      currentSessionId = storedBoard.sessionId;
      grid = storedBoard.grid;
      wordBank = storedBoard.wordBank;
      hintPositions = storedBoard.hints || [];
    } else {
      // Fetch grid from backend
      const deviceId = localStorage.getItem('kassie_device_id');
      response = await api.get(`/api/device/word-search/${module.id}/play`, {
        deviceId: deviceId,
      });

      currentSessionId = response.session_id;
      grid = response.grid;
      wordBank = response.word_bank;
      hintPositions = response.hints || [];
      currentDifficulty = response.difficulty || module.difficulty || 'medium';

      // Store the board in unified module cache
      setCachedModule(module.id, {
        wordSearch: {
          grid: response.grid,
          wordBank: response.word_bank,
          rows: response.rows,
          cols: response.cols,
          sessionId: response.session_id,
          hints: response.hints || [],
        },
        fetch_complete: true,
        latest: true
      });
    }

    startTime = Date.now();

    // Render the game
    renderGame(contentContainer, profile, module, response);

    // Hide Kassie loader once puzzle is displayed
    window.hideLoader();
  } catch (error) {
    console.error('❌ Failed to load word search:', error);

    // Hide Kassie loader on error
    window.hideLoader();

    contentContainer.innerHTML = `
      <div class="word-search-container">
        <div class="word-search-error">
          <p>${t('child.wordSearch.loadError') || 'Could not load puzzle. Try again.'}</p>
          <button class="ws-back-btn" id="wsBackBtn">
            ${t('child.navigation.back') || 'Back'}
          </button>
        </div>
      </div>
    `;
    document.getElementById('wsBackBtn')?.addEventListener('click', async () => {
      const { router } = await import('../utils/router.js');
      await router.navigate('library', profile);
    });
  }
}

/**
 * Render the game UI
 */
function renderGame(container, profile, module, data) {
  const rows = data.rows;
  const cols = data.cols;

  container.innerHTML = `
    <div class="word-search-container">
      <!-- Header -->
      <div class="ws-header">
        <div class="ws-title">${module.title || 'Woordsoektog'}</div>
        <div class="ws-hint-area">
          <button class="ws-hint-btn" id="wsHintBtn">
            <img src="/svg/components/WordsearchHint-1.svg" alt="Hint" />
          </button>
          <div class="ws-hint-dots" id="wsHintDots">
            <span class="ws-hint-dot ${hintsUsed > 0 ? 'ws-hint-dot--used' : 'ws-hint-dot--available'}"></span>
            <span class="ws-hint-dot ${hintsUsed > 1 ? 'ws-hint-dot--used' : 'ws-hint-dot--available'}"></span>
            <span class="ws-hint-dot ${hintsUsed > 2 ? 'ws-hint-dot--used' : 'ws-hint-dot--available'}"></span>
          </div>
        </div>
      </div>

      <!-- Grid -->
      <div class="ws-grid-wrapper">
        <div class="ws-grid" id="wsGrid"
             style="grid-template-columns: repeat(${cols}, 1fr); grid-template-rows: repeat(${rows}, 1fr);"
             data-cols="${cols}" data-rows="${rows}">
          ${renderGridCells(grid, rows, cols)}
        </div>
      </div>

      <!-- Word Bank -->
      <div class="ws-word-bank" id="wsWordBank">
        ${wordBank.map((w) => `<span class="ws-word" data-word="${w.toUpperCase()}">${w}</span>`).join('')}
      </div>
    </div>
  `;

  bindEvents(container, profile, module);

  // Restore previously found words from localStorage (Req 14)
  restoreProgress(container, profile, module);
}

/**
 * Render grid cells as HTML
 */
function renderGridCells(grid, rows, cols) {
  let html = '';
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      html += `<div class="ws-cell" data-row="${r}" data-col="${c}">${grid[r][c]}</div>`;
    }
  }
  return html;
}

/**
 * Bind touch/pointer events for drag selection
 */
function bindEvents(container, profile, module) {
  const gridEl = container.querySelector('#wsGrid');
  const hintBtn = container.querySelector('#wsHintBtn');

  // Hint button
  hintBtn.addEventListener('click', () => {
    giveHint(container, profile, module);
  });

  // Touch/pointer events on grid
  let startCell = null;

  gridEl.addEventListener('pointerdown', (e) => {
    ensureAudioUnlocked();
    initAudioElements();
    const cell = e.target.closest('.ws-cell');
    if (!cell) return;
    e.preventDefault();
    isDragging = true;
    startCell = { row: parseInt(cell.dataset.row), col: parseInt(cell.dataset.col) };
    selectedCells = [startCell];
    updateSelectionHighlight(container, selectedCells);
    gridEl.setPointerCapture(e.pointerId);
  });

  gridEl.addEventListener('pointermove', (e) => {
    if (!isDragging || !startCell) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el || !el.classList.contains('ws-cell')) return;

    const endRow = parseInt(el.dataset.row);
    const endCol = parseInt(el.dataset.col);

    // Compute all cells along the line from start to current
    const lineCells = getCellsOnLine(startCell.row, startCell.col, endRow, endCol);
    if (lineCells.length > 0) {
      selectedCells = lineCells;
      updateSelectionHighlight(container, selectedCells);
    }
  });

  gridEl.addEventListener('pointerup', (e) => {
    if (!isDragging) return;
    isDragging = false;
    startCell = null;
    gridEl.releasePointerCapture(e.pointerId);
    checkSelection(container, profile, module);
  });
}

/**
 * Get all cells along a straight line from (r1,c1) to (r2,c2)
 * Returns empty array if the line is not horizontal, vertical, or diagonal
 */
function getCellsOnLine(r1, c1, r2, c2) {
  const dr = r2 - r1;
  const dc = c2 - c1;

  // Must be a straight line (horizontal, vertical, or 45-degree diagonal)
  if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) {
    return []; // Not a valid direction
  }

  const steps = Math.max(Math.abs(dr), Math.abs(dc));
  if (steps === 0) return [{ row: r1, col: c1 }];

  const stepR = dr === 0 ? 0 : dr > 0 ? 1 : -1;
  const stepC = dc === 0 ? 0 : dc > 0 ? 1 : -1;

  const cells = [];
  for (let i = 0; i <= steps; i++) {
    cells.push({ row: r1 + i * stepR, col: c1 + i * stepC });
  }
  return cells;
}

/**
 * Update visual selection highlight
 */
function updateSelectionHighlight(container, cells) {
  // Clear previous selection
  container.querySelectorAll('.ws-cell--selected').forEach((el) => {
    el.classList.remove('ws-cell--selected');
  });
  // Highlight current selection
  cells.forEach((c) => {
    const cell = container.querySelector(`.ws-cell[data-row="${c.row}"][data-col="${c.col}"]`);
    if (cell && !cell.classList.contains('ws-cell--found')) {
      cell.classList.add('ws-cell--selected');
    }
  });
}

/**
 * Check if the current selection forms a valid word
 */
function checkSelection(container, profile, module) {
  // Build selected word from cells
  const selectedWord = selectedCells.map((c) => grid[c.row][c.col]).join('');

  // Check against word bank (case-insensitive)
  const match = wordBank.find((w) => {
    const normalized = w
      .toUpperCase()
      .replace(/[êëéè]/gi, 'E')
      .replace(/[îï]/gi, 'I')
      .replace(/[\s\-']/g, '');
    return normalized === selectedWord || normalized === selectedWord.split('').reverse().join('');
  });

  if (match && !foundWords.has(match.toUpperCase())) {
    // Word found!
    foundWords.add(match.toUpperCase());
    markFound(container, match, profile, module);

    // Check if all words found
    if (foundWords.size === wordBank.length) {
      // Go straight to completion screen (user gesture still active for audio)
      showCompletion(container, profile, module);
    }
  } else {
    // Clear selection
    clearSelection(container);
  }
}

/**
/** Word colours for found state — read from CSS variables defined in variables.css */
const WORD_COLORS = (() => {
  const root = document.documentElement;
  const style = getComputedStyle(root);
  return [
    style.getPropertyValue('--ws-bg-pastel1').trim() || '#ff6b6b',
    style.getPropertyValue('--ws-bg-pastel2').trim() || '#4ecdc4',
    style.getPropertyValue('--ws-bg-pastel3').trim() || '#45b7d1',
    style.getPropertyValue('--ws-bg-pastel4').trim() || '#96ceb4',
    style.getPropertyValue('--ws-bg-pastel5').trim() || '#ffeaa7',
    style.getPropertyValue('--ws-bg-pastel6').trim() || '#dda0dd',
    style.getPropertyValue('--ws-bg-pastel7').trim() || '#98d8c8',
    style.getPropertyValue('--ws-bg-pastel8').trim() || '#f7dc6f',
    style.getPropertyValue('--ws-bg-pastel9').trim() || '#bb8fce',
    style.getPropertyValue('--ws-bg-pastel10').trim() || '#85c1e9',
    style.getPropertyValue('--ws-bg-pastel11').trim() || '#f1948a',
    style.getPropertyValue('--ws-bg-pastel12').trim() || '#7dcea0',
  ];
})();
let colorIndex = 0;

/**
 * Mark a word as found — permanently highlight cells with unique colour
 * Persists progress to localStorage (Req 14)
 */
function markFound(container, word, profile, module) {
  const color = WORD_COLORS[colorIndex % WORD_COLORS.length];
  colorIndex++;

  // Capture cells before clearing selectedCells
  const cells = selectedCells.map((c) => ({ row: c.row, col: c.col }));

  // Collect cell DOM elements for animation
  const cellElements = selectedCells.map((c) =>
    container.querySelector(`.ws-cell[data-row="${c.row}"][data-col="${c.col}"]`)
  ).filter(Boolean);

  // Keep cells highlighted with this word's colour
  selectedCells.forEach((c) => {
    const cell = container.querySelector(`.ws-cell[data-row="${c.row}"][data-col="${c.col}"]`);
    if (cell) {
      cell.classList.remove('ws-cell--selected');
      cell.classList.add('ws-cell--found');
      cell.style.background = color;
      cell.style.color = '#000';
    }
  });
  selectedCells = [];

  // Cross out word in bank with matching colour
  const wordEl = container.querySelector(`.ws-word[data-word="${word.toUpperCase()}"]`);
  if (wordEl) {
    wordEl.classList.add('ws-word--found');
    wordEl.style.background = color;
  }

  // Play reward sound (pre-created for iOS) + visual animation
  playRewardSoundLocal();
  playWordFoundReward(cellElements, currentDifficulty);

  // Persist progress to localStorage (Req 14)
  if (profile && module) {
    const progress = getStoredProgress(profile.id, module.id);
    progress.foundWords.push({ word: word.toUpperCase(), cells, color });
    storeProgress(profile.id, module.id, progress);
  }
}

/**
 * Clear current selection
 */
function clearSelection(container) {
  container.querySelectorAll('.ws-cell--selected').forEach((el) => {
    el.classList.remove('ws-cell--selected');
  });
  selectedCells = [];
}

/**
 * Give a hint — highlight first letter of an unfound word
 * Updates hint dots: pink → kassie-blue as each hint is used
 * Persists hints to localStorage so they survive navigation
 */
function giveHint(container, profile, module) {
  const unfound = wordBank.filter((w) => !foundWords.has(w.toUpperCase()));
  if (unfound.length === 0) return;
  if (hintsUsed >= 3) return; // Max 3 hints

  hintsUsed++;

  // Persist to localStorage
  storeHints(profile.id, module.id, hintsUsed);

  // Update hint dots — change the used dot from pink to blue
  const dots = container.querySelectorAll('.ws-hint-dot');
  if (dots[hintsUsed - 1]) {
    dots[hintsUsed - 1].classList.remove('ws-hint-dot--available');
    dots[hintsUsed - 1].classList.add('ws-hint-dot--used');
  }

  // Pick a random unfound word
  const word = unfound[Math.floor(Math.random() * unfound.length)].toUpperCase();

  // Find its actual position from hint data
  const hintData = hintPositions.find((h) => h.word === word);
  if (hintData) {
    // Highlight the actual first letter cell of this word
    const cell = container.querySelector(
      `.ws-cell[data-row="${hintData.row}"][data-col="${hintData.col}"]`
    );
    if (cell && !cell.classList.contains('ws-cell--found')) {
      cell.classList.add('ws-cell--hint');
      setTimeout(() => cell.classList.remove('ws-cell--hint'), 2500);
    }
  }
}

/**
 * Show completion screen and submit results
 */
async function showCompletion(container, profile, module) {
  const timeSeconds = Math.round((Date.now() - startTime) / 1000);
  const stars = Math.max(1, 3 - hintsUsed);

  // Clear persisted progress — game is complete (Req 14)
  clearProgress(profile.id, module.id);

  // Clear persisted hints — next replay starts with full hints
  try {
    localStorage.removeItem(getHintStorageKey(profile.id, module.id));
  } catch (e) {
    console.warn('Could not clear hint storage:', e);
  }

  // Clear cached board — all children get a fresh puzzle next time
  setCachedModule(module.id, { wordSearch: null, fetch_complete: false });

  // Submit to backend
  try {
    const deviceId = localStorage.getItem('kassie_device_id');
    await api.post(
      `/api/device/word-search/${module.id}/complete`,
      {
        session_id: currentSessionId,
        found_words: Array.from(foundWords),
        time_seconds: timeSeconds,
        hints_used: hintsUsed,
      },
      { deviceId }
    );
  } catch (error) {
    console.error('Failed to submit completion:', error);
  }

  // Show completion UI
  const wrapper = container.querySelector('.ws-grid-wrapper');
  if (wrapper) {
    // Build hint dots display for completion screen (pink = available, blue = used)
    const hintDotsHtml = Array.from({ length: 3 }, (_, i) => {
      const dotClass = i < hintsUsed ? 'ws-hint-dot--used' : 'ws-hint-dot--available';
      return `<span class="ws-hint-dot ${dotClass}"></span>`;
    }).join('');

    // Hide hint button and dots on completion
    const hintArea = container.querySelector('.ws-hint-area');
    if (hintArea) hintArea.style.display = 'none';

    // Hide word bank on completion — the words are already found
    const wordBankEl = container.querySelector('.ws-word-bank');
    if (wordBankEl) wordBankEl.style.display = 'none';

    // Hide title on completion screen
    const titleEl = container.querySelector('.ws-title');
    if (titleEl) titleEl.style.display = 'none';

    wrapper.innerHTML = `
      <div class="ws-completion">
        <img class="ws-completion-image" src="/svg/branding/Kassiewoohoo.svg" alt="Woohoo!" style="width: clamp(200px, 50vw, 400px); height: auto;"/>
        <h2>${t('child.wordSearch.complete') || 'Well done!'}</h2>
        <div class="ws-completion-stars">
          <img src="/svg/components/YellowStar.svg" alt="⭐" width="72" height="72" class="ws-completion-star-pulse" id="wsCompletionStar" />
        </div>
        <div class="ws-completion-hints">
          <span class="ws-completion-hints-label">${t('child.wordSearch.hintsUsed') || 'Hints'}:</span>
          <div class="ws-hint-dots">${hintDotsHtml}</div>
        </div>
        <p>${t('child.wordSearch.time') || 'Time'}: ${timeSeconds}s</p>
        <div class="ws-completion-actions">
          <button class="ws-back-btn" id="wsBackToLibrary">
            <img src="/svg/icons/LeftArrow.svg" alt="" height="24" style="height:24px;width:auto;" /> ${t('child.navigation.back') || 'Terug'}
          </button>
          <button class="ws-play-again-btn" id="wsPlayAgain">
            <img src="/svg/icons/Reload.svg" alt="" width="24" height="24" /> ${t('child.wordSearch.playAgain') || 'Speel Weer'}
          </button>
        </div>
      </div>
    `;

    document.getElementById('wsPlayAgain')?.addEventListener('click', () => {
      // Clear cached board so a fresh grid is fetched
      setCachedModule(module.id, { wordSearch: null, fetch_complete: false });
      renderWordSearchPlayer(profile, module);
    });
    document.getElementById('wsBackToLibrary')?.addEventListener('click', async () => {
      const { router } = await import('../utils/router.js');
      await router.navigate('library', profile);
    });

    // Play win sound + 10 colorful stars from bottom, then bounce + yellow star to achievements
    playWsCompletionSequence();
  }
}

/**
 * Word search game over animation sequence:
 * 1. Play win.mp3 + launch 10 colorful stars from bottom (simultaneously)
 * 2. When both audio and stars finish, play bounce.mp3 + fly yellow star to achievements
 */
function playWsCompletionSequence() {
  let audioFinished = false;
  let starsFinished = false;

  function onBothDone() {
    if (!audioFinished || !starsFinished) return;
    // Play bounce sound and fly the yellow star
    playBounceSound();
    animateCompletionStar();
  }

  // 1. Play win.mp3 immediately (use pre-created element for iOS)
  try {
    if (audioWin) {
      audioWin.currentTime = 0;
      audioWin.play().catch(() => { audioFinished = true; onBothDone(); });
      audioWin.addEventListener('ended', () => {
        audioFinished = true;
        onBothDone();
      }, { once: true });
    } else {
      audioFinished = true;
      onBothDone();
    }
    // Fallback if ended never fires
    setTimeout(() => { audioFinished = true; onBothDone(); }, 4000);
  } catch {
    audioFinished = true;
    onBothDone();
  }

  // 2. Launch 10 colorful stars from bottom
  playWsBottomStars(() => {
    starsFinished = true;
    onBothDone();
  });
}

/**
 * Launch 10 colorful stars from the bottom of the screen toward the achievements icon.
 * Stars are 50px apart on desktop, 25px on mobile.
 * @param {Function} onComplete - Called when all stars have finished
 */
function playWsBottomStars(onComplete) {
  const STAR_URLS = [
    '/svg/components/YellowStar1.svg',
    '/svg/components/RedStar1.svg',
    '/svg/components/PinkStar1.svg',
    '/svg/components/GreenStar1.svg',
    '/svg/components/BlueStar1.svg',
  ];

  const isMobile = window.innerWidth < 768;
  const starCount = 10;
  const spacing = isMobile ? 25 : 50;
  const starSize = isMobile ? 25 : 50;
  const totalWidth = (starCount - 1) * spacing;
  const startX = (window.innerWidth - totalWidth) / 2;
  const startY = window.innerHeight - (isMobile ? 60 : 80);

  const achievementsBtn = document.querySelector('[data-route="achievements"]');
  const destRect = achievementsBtn
    ? achievementsBtn.getBoundingClientRect()
    : { left: window.innerWidth - 60, top: 30, width: 40, height: 40 };
  const destX = destRect.left + destRect.width / 2;
  const destY = destRect.top + destRect.height / 2;

  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '10000',
    overflow: 'hidden',
  });
  document.body.appendChild(container);

  let completed = 0;

  for (let i = 0; i < starCount; i++) {
    setTimeout(() => {
      const starUrl = STAR_URLS[Math.floor(Math.random() * STAR_URLS.length)];
      const ox = startX + i * spacing;
      const oy = startY;

      const el = document.createElement('img');
      el.src = starUrl;
      el.alt = '';
      el.setAttribute('aria-hidden', 'true');
      Object.assign(el.style, {
        position: 'absolute',
        width: starSize + 'px',
        height: starSize + 'px',
        objectFit: 'contain',
        pointerEvents: 'none',
        left: '0',
        top: '0',
        opacity: '1',
      });
      container.appendChild(el);

      const midY = oy - (oy / 2);
      const amplitude = 15 + Math.random() * 30;
      const frequency = 1.5 + Math.random() * 2.5;
      const phaseOffset = Math.random() * Math.PI * 2;
      const travelDuration = 1200 + Math.random() * 400;
      const rotation = Math.random() * 360;
      const endSize = isMobile ? 8 : 10;
      const midSize = isMobile ? 40 : 80;
      const animStart = performance.now();

      function tick(now) {
        const elapsed = now - animStart;
        const t = Math.min(elapsed / travelDuration, 1);

        let x, y, size, opacity;

        if (t <= 0.5) {
          const phase = t / 0.5;
          const eased = 1 - Math.pow(1 - phase, 2);
          const wobble = Math.sin(frequency * Math.PI * 2 * phase + phaseOffset) * amplitude * (1 - phase * 0.3);
          x = ox + wobble;
          y = oy + (midY - oy) * eased;
          size = starSize + (midSize - starSize) * eased;
          opacity = 1;
        } else {
          const phase = (t - 0.5) / 0.5;
          const eased = phase * phase;
          x = ox + (destX - ox) * eased;
          y = midY + (destY - midY) * eased;
          size = midSize + (endSize - midSize) * eased;
          opacity = 1 - phase;
        }

        const currentRotation = rotation + t * 360;
        el.style.transform = `translate(${x - size / 2}px, ${y - size / 2}px) rotate(${currentRotation}deg)`;
        el.style.width = size + 'px';
        el.style.height = size + 'px';
        el.style.opacity = opacity;

        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          el.remove();
          completed++;
          if (completed >= starCount) {
            container.remove();
            if (onComplete) onComplete();
          }
        }
      }

      requestAnimationFrame(tick);
    }, i * 300);
  }
}

/**
 * Animate the single completion star: pulse double-normal-double-normal,
 * then spawn a copy that flies to the achievements icon with fade.
 */
function animateCompletionStar() {
  const star = document.getElementById('wsCompletionStar');
  if (!star) return;
  star.dataset.animated = 'true';

  // Pulse sequence: double, normal, double, normal (4 beats)
  let pulseCount = 0;
  const pulseInterval = setInterval(() => {
    pulseCount++;
    if (pulseCount % 2 === 1) {
      star.style.transition = 'transform 250ms ease-out';
      star.style.transform = 'scale(2) rotate(-1deg)';
    } else {
      star.style.transition = 'transform 250ms ease-in';
      star.style.transform = 'scale(1) rotate(-1deg)';
    }
    if (pulseCount >= 4) {
      clearInterval(pulseInterval);
      // Play bounce sound when star starts flying
      playBounceSound();
      // Wave the Kassiewoohoo image (rotate 15deg clockwise and back over 2s)
      waveKassieWoohoo();
      // After pulse: spawn a flying copy
      setTimeout(() => flyStarToAchievements(star), 300);
    }
  }, 300);
}

/**
 * Animate the Kassiewoohoo image in a wave motion:
 * rotate 15deg clockwise, then back to 0, over 2 seconds.
 */
function waveKassieWoohoo() {
  const img = document.querySelector('.ws-completion-image');
  if (!img) return;

  img.style.transition = 'transform 0.5s ease-in-out';
  img.style.transform = 'rotate(15deg)';

  setTimeout(() => {
    img.style.transform = 'rotate(-15deg)';
  }, 500);

  setTimeout(() => {
    img.style.transform = 'rotate(10deg)';
  }, 1000);

  setTimeout(() => {
    img.style.transform = 'rotate(-5deg)';
  }, 1400);

  setTimeout(() => {
    img.style.transform = 'rotate(0deg)';
  }, 1800);
}

/**
 * Create a copy of the star and animate it flying to the achievements icon.
 * Fades from opacity 1 to 0 when within 100px of the icon.
 */
function flyStarToAchievements(originalStar) {
  const starRect = originalStar.getBoundingClientRect();
  const ox = starRect.left + starRect.width / 2;
  const oy = starRect.top + starRect.height / 2;

  // Find achievements icon position
  const achievementsBtn = document.querySelector('[data-route="achievements"]');
  const destRect = achievementsBtn
    ? achievementsBtn.getBoundingClientRect()
    : { left: window.innerWidth - 60, top: 30, width: 40, height: 40 };
  const destX = destRect.left + destRect.width / 2;
  const destY = destRect.top + destRect.height / 2;

  // Create flying star copy
  const flyStar = document.createElement('img');
  flyStar.src = '/svg/components/YellowStar.svg';
  flyStar.alt = '';
  flyStar.setAttribute('aria-hidden', 'true');
  Object.assign(flyStar.style, {
    position: 'fixed',
    width: '72px',
    height: '72px',
    objectFit: 'contain',
    pointerEvents: 'none',
    zIndex: '10001',
    left: '0',
    top: '0',
    opacity: '1',
  });
  document.body.appendChild(flyStar);

  const travelDuration = 1200;
  const startTime = performance.now();

  // Total distance for fade calculation
  const totalDist = Math.sqrt(Math.pow(destX - ox, 2) + Math.pow(destY - oy, 2));

  function tick(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / travelDuration, 1);
    const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic

    const x = ox + (destX - ox) * eased;
    const y = oy + (destY - oy) * eased;

    // Size shrinks from 72 to 36
    const size = 72 - 36 * eased;

    // Calculate remaining distance to destination
    const remainingDist = Math.sqrt(Math.pow(destX - x, 2) + Math.pow(destY - y, 2));

    // Fade starts when within 100px of destination
    let opacity = 1;
    if (remainingDist < 100) {
      opacity = remainingDist / 100;
    }

    flyStar.style.transform = `translate(${x - size / 2}px, ${y - size / 2}px) rotate(${t * 359}deg)`;
    flyStar.style.width = size + 'px';
    flyStar.style.height = size + 'px';
    flyStar.style.opacity = opacity;

    if (t < 1) {
      requestAnimationFrame(tick);
    } else {
      flyStar.remove();
    }
  }

  requestAnimationFrame(tick);
}

/**
 * Get the localStorage key for storing hints per profile + module
 * @param {string} profileId
 * @param {string} moduleId
 * @returns {string}
 */
function getHintStorageKey(profileId, moduleId) {
  return `kassie_ws_hints_${profileId}_${moduleId}`;
}

/**
 * Get stored hint count from localStorage
 * @param {string} profileId
 * @param {string} moduleId
 * @returns {number} Number of hints already used (0-3)
 */
function getStoredHints(profileId, moduleId) {
  try {
    const stored = localStorage.getItem(getHintStorageKey(profileId, moduleId));
    const count = parseInt(stored, 10);
    return isNaN(count) ? 0 : Math.min(count, 3);
  } catch {
    return 0;
  }
}

/**
 * Persist hint count to localStorage
 * @param {string} profileId
 * @param {string} moduleId
 * @param {number} count
 */
function storeHints(profileId, moduleId, count) {
  try {
    localStorage.setItem(getHintStorageKey(profileId, moduleId), String(count));
  } catch (e) {
    console.warn('Could not persist hint count:', e);
  }
}

/**
 * Get the localStorage key for storing game progress per profile + module (Req 14)
 * @param {string} profileId
 * @param {string} moduleId
 * @returns {string}
 */
function getProgressKey(profileId, moduleId) {
  return `kassie_ws_progress_${profileId}_${moduleId}`;
}

/**
 * Get stored game progress from localStorage (Req 14)
 * @param {string} profileId
 * @param {string} moduleId
 * @returns {{foundWords: Array<{word: string, cells: Array<{row: number, col: number}>, color: string}>}}
 */
function getStoredProgress(profileId, moduleId) {
  try {
    const stored = localStorage.getItem(getProgressKey(profileId, moduleId));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && Array.isArray(parsed.foundWords)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { foundWords: [] };
}

/**
 * Persist game progress to localStorage (Req 14)
 * @param {string} profileId
 * @param {string} moduleId
 * @param {{foundWords: Array}} progress
 */
function storeProgress(profileId, moduleId, progress) {
  try {
    localStorage.setItem(getProgressKey(profileId, moduleId), JSON.stringify(progress));
  } catch (e) {
    console.warn('Could not persist word search progress:', e);
  }
}

/**
 * Clear stored game progress on completion (Req 14)
 * @param {string} profileId
 * @param {string} moduleId
 */
function clearProgress(profileId, moduleId) {
  try {
    localStorage.removeItem(getProgressKey(profileId, moduleId));
  } catch (e) {
    console.warn('Could not clear word search progress:', e);
  }
}

/**
 * Restore previously found words from localStorage (Req 14)
 * Re-applies highlights to the grid and crosses out words in the bank
 * @param {HTMLElement} container
 * @param {object} profile
 * @param {object} module
 */
function restoreProgress(container, profile, module) {
  const progress = getStoredProgress(profile.id, module.id);
  if (progress.foundWords.length === 0) return;

  console.log(`🔄 Restoring ${progress.foundWords.length} found words from previous session`);

  progress.foundWords.forEach((entry) => {
    // Add to foundWords set
    foundWords.add(entry.word);

    // Advance colorIndex to stay in sync
    colorIndex++;

    // Highlight cells on grid
    entry.cells.forEach((c) => {
      const cell = container.querySelector(`.ws-cell[data-row="${c.row}"][data-col="${c.col}"]`);
      if (cell) {
        cell.classList.add('ws-cell--found');
        cell.style.background = entry.color;
        cell.style.color = '#000';
      }
    });

    // Cross out word in bank
    const wordEl = container.querySelector(`.ws-word[data-word="${entry.word}"]`);
    if (wordEl) {
      wordEl.classList.add('ws-word--found');
      wordEl.style.background = entry.color;
    }
  });
}
